// === SYSTÈME DE TIRAGE DE LOOT ===
// Logique pure pour pondérer la rareté du loot selon le type de combat.
// Sprint 76 (Phase 7) : différencie drop rates pour valoriser élites/boss.

export type CombatLootContext = 'normal' | 'elite' | 'boss';
export type LootRarity = 'common' | 'uncommon' | 'rare';

/**
 * Distribution de rareté par type de combat.
 * Les valeurs sont les seuils cumulés [common, common+uncommon, common+uncommon+rare=1].
 *
 * - normal : 70/25/5    → loot moyen, gros volume (zombies de base)
 * - elite  : 35/45/20   → vrais bonus, vaut le coup
 * - boss   : 15/50/35   → drops premium garantis
 */
const RARITY_WEIGHTS: Record<CombatLootContext, { common: number; uncommon: number; rare: number }> = {
  normal: { common: 0.70, uncommon: 0.25, rare: 0.05 },
  elite:  { common: 0.35, uncommon: 0.45, rare: 0.20 },
  boss:   { common: 0.15, uncommon: 0.50, rare: 0.35 },
};

/**
 * Tire une rareté en fonction du contexte de combat.
 * @param context Type de combat (normal/elite/boss)
 * @param rng Générateur aléatoire (pour tests)
 */
export function rollRarity(
  context: CombatLootContext,
  rng: () => number = Math.random,
): LootRarity {
  const weights = RARITY_WEIGHTS[context];
  const roll = rng();
  if (roll < weights.common) return 'common';
  if (roll < weights.common + weights.uncommon) return 'uncommon';
  return 'rare';
}

/**
 * Nombre de récompenses que le joueur peut prendre selon le contexte.
 * Boss = 2 récompenses (item+heal, 2 items, ou item+conso). Autres = 1.
 */
export function maxPicksForContext(context: CombatLootContext): number {
  return context === 'boss' ? 2 : 1;
}

/**
 * Garantit qu'au moins un item rare apparaît dans le pool affiché aux boss.
 * Renvoie un tableau de 3 raretés : pour boss, force au moins une 'rare'.
 */
export function rollLootPool(
  context: CombatLootContext,
  count: number = 3,
  rng: () => number = Math.random,
): LootRarity[] {
  const rarities: LootRarity[] = [];
  for (let i = 0; i < count; i++) {
    rarities.push(rollRarity(context, rng));
  }
  // Boss : si aucun rare tiré, force le premier slot en rare
  if (context === 'boss' && !rarities.includes('rare')) {
    rarities[0] = 'rare';
  }
  return rarities;
}
